import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import type { Activity } from "./ActivitiesList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActivityFormValues {
  activityDate: string;
  activityTime: string;
  duration: string;
  zipCode: string;
  notes: string;
  isBedNight: boolean;
  isHotelBedNight: boolean;
  isResidenceBedNight: boolean;
  isTransitionalHousing: boolean;
}

interface ActivityFormProps {
  personId: string;
  activity?: Activity | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActivityForm({ personId, activity, onClose }: ActivityFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!activity;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ActivityFormValues>({
    defaultValues: {
      activityDate:
        activity?.activity_date ?? new Date().toISOString().split("T")[0],
      activityTime: activity?.activity_time ?? "",
      duration: activity?.duration != null ? String(activity.duration) : "",
      zipCode: activity?.zip_code ?? "",
      notes: activity?.notes ?? "",
      isBedNight: activity?.is_bed_night === 1,
      isHotelBedNight: activity?.is_hotel_bed_night === 1,
      isResidenceBedNight: activity?.is_residence_bed_night === 1,
      isTransitionalHousing: activity?.is_transitional_housing === 1,
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ActivityFormValues) =>
      api.post(`/clients/${personId}/activities`, {
        activityDate: data.activityDate,
        activityTime: data.activityTime || undefined,
        duration: data.duration ? parseFloat(data.duration) : undefined,
        zipCode: data.zipCode || undefined,
        notes: data.notes || undefined,
        isBedNight: data.isBedNight,
        isHotelBedNight: data.isHotelBedNight,
        isResidenceBedNight: data.isResidenceBedNight,
        isTransitionalHousing: data.isTransitionalHousing,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", personId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ActivityFormValues) =>
      api.put(`/clients/${personId}/activities/${activity!.id}`, {
        activityDate: data.activityDate,
        activityTime: data.activityTime || undefined,
        duration: data.duration ? parseFloat(data.duration) : undefined,
        zipCode: data.zipCode || undefined,
        notes: data.notes || undefined,
        isBedNight: data.isBedNight,
        isHotelBedNight: data.isHotelBedNight,
        isResidenceBedNight: data.isResidenceBedNight,
        isTransitionalHousing: data.isTransitionalHousing,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", personId] });
      onClose();
    },
  });

  const mutation = isEditing ? updateMutation : createMutation;

  function onSubmit(data: ActivityFormValues) {
    mutation.mutate(data);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {isEditing ? "Edit Activity" : "New Activity"}
        </h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Date"
                type="date"
                required
                {...register("activityDate", {
                  required: "Date is required",
                })}
                error={errors.activityDate?.message}
              />
              <Input
                label="Time"
                type="time"
                {...register("activityTime")}
              />
              <Input
                label="Duration (hours)"
                type="number"
                step="0.25"
                min="0"
                placeholder="e.g. 1.5"
                {...register("duration")}
                error={errors.duration?.message}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Zip Code"
                maxLength={10}
                placeholder="e.g. 12345"
                {...register("zipCode")}
              />
            </div>

            {/* Bed night checkboxes */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-foreground">
                Bed Night
              </legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("isBedNight")}
                    className="h-4 w-4 rounded border-input"
                  />
                  Standard
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("isHotelBedNight")}
                    className="h-4 w-4 rounded border-input"
                  />
                  Hotel
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("isResidenceBedNight")}
                    className="h-4 w-4 rounded border-input"
                  />
                  Residence
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("isTransitionalHousing")}
                    className="h-4 w-4 rounded border-input"
                  />
                  Transitional Housing
                </label>
              </div>
            </fieldset>

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Notes
              </label>
              <textarea
                {...register("notes")}
                rows={4}
                placeholder="Activity notes..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* Error */}
            {mutation.isError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {(mutation.error as Error)?.message ?? "An error occurred"}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={mutation.isPending}>
                {isEditing ? "Save Changes" : "Create Activity"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
